import { useCallback, useState } from 'react';
import { placeService } from '../services';
import type {
  PaginatedPlaces,
  Place,
  PlaceCreate,
  PlaceListParams,
  PlaceScoreHistoryResponse,
  PlaceUpdate,
} from '../types';

export function usePlaces() {
  const [places, setPlaces] = useState<PaginatedPlaces | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlaces = useCallback(async (params: PlaceListParams = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await placeService.listPlaces(params);
      setPlaces(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : '플레이스 목록을 불러오는데 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    places,
    isLoading,
    error,
    fetchPlaces,
  };
}

export function usePlace() {
  const [place, setPlace] = useState<Place | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlace = useCallback(async (placeId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await placeService.getPlace(placeId);
      setPlace(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : '플레이스를 불러오는데 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createPlace = useCallback(async (data: PlaceCreate) => {
    setIsLoading(true);
    setError(null);
    try {
      const created = await placeService.createPlace(data);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : '플레이스 등록에 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updatePlace = useCallback(async (placeId: number, data: PlaceUpdate) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await placeService.updatePlace(placeId, data);
      setPlace(updated);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : '플레이스 수정에 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deletePlace = useCallback(async (placeId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await placeService.deletePlace(placeId);
      setPlace(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : '플레이스 삭제에 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    place,
    isLoading,
    error,
    fetchPlace,
    createPlace,
    updatePlace,
    deletePlace,
  };
}

export function usePlaceScoreHistory() {
  const [history, setHistory] = useState<PlaceScoreHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(
    async (
      placeId: number,
      params?: { start_date?: string; end_date?: string; period?: '7d' | '30d' | '90d' }
    ) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await placeService.getPlaceScoreHistory(placeId, params);
        setHistory(data);
        return data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'GDID 히스토리를 불러오는데 실패했습니다';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    history,
    isLoading,
    error,
    fetchHistory,
  };
}
